import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

// ECDEV-169: one uploaded attachment the gallery can page through.
export interface GalleryItem {
  id: string;
  name: string;
  contentType: string;
  file: Uint8Array;
}

@Component({
  selector: 'app-attachments-gallery',
  templateUrl: './attachments-gallery.component.html',
  styleUrls: ['./attachments-gallery.component.scss'],
})
export class AttachmentsGalleryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() items: GalleryItem[] = [];
  @Input() startIndex = 0;
  @Output() modalState = new EventEmitter<{ isClose: boolean }>();

  index = 0;
  pdfSrc: Uint8Array | null = null;
  imageUrl: string | null = null;
  private printFrame: HTMLIFrameElement | null = null;
  private printUrl: string | null = null;

  ngOnInit(): void {
    this.setIndex(this.startIndex);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-anchor when the caller opens the gallery at a new item / list.
    if (changes['startIndex'] || changes['items']) {
      this.setIndex(this.startIndex);
    }
  }

  get current(): GalleryItem | undefined {
    return this.items[this.index];
  }

  get isPdf(): boolean {
    const item = this.current;
    return !!item && this.isPdfItem(item);
  }

  private isPdfItem(item: GalleryItem): boolean {
    return (
      item.contentType === 'application/pdf' ||
      item.name.toLowerCase().endsWith('.pdf')
    );
  }

  // Typed as PDF whenever the gallery renders it as one, so a PDF uploaded with a generic content
  // type still opens in the browser's PDF viewer when printed.
  private blobOf(item: GalleryItem): Blob {
    return new Blob([item.file], {
      type: this.isPdfItem(item) ? 'application/pdf' : item.contentType,
    });
  }

  private setIndex(i: number): void {
    if (!this.items.length) {
      return;
    }
    this.index = Math.min(Math.max(i, 0), this.items.length - 1);
    this.revokeImageUrl();
    const item = this.items[this.index];
    if (this.isPdf) {
      // Pass a fresh copy so pdf.js can take ownership of the buffer without
      // detaching the original (lets us re-render the same file on navigate back).
      this.pdfSrc = new Uint8Array(item.file);
      this.imageUrl = null;
    } else {
      this.pdfSrc = null;
      this.imageUrl = URL.createObjectURL(
        new Blob([item.file], { type: item.contentType })
      );
    }
  }

  prev(event?: Event): void {
    event?.stopPropagation();
    if (this.index > 0) {
      this.setIndex(this.index - 1);
    }
  }

  next(event?: Event): void {
    event?.stopPropagation();
    if (this.index < this.items.length - 1) {
      this.setIndex(this.index + 1);
    }
  }

  // ECDEV-431: save the attachment on screen under the name it was uploaded with. Unlike the
  // required documents' preview, nothing is recorded on the order — attachments are not tracked.
  download(event?: Event): void {
    event?.stopPropagation();
    const item = this.current;
    if (!item) {
      return;
    }
    const url = URL.createObjectURL(this.blobOf(item));
    const anchorEl = document.createElement('a');
    anchorEl.href = url;
    anchorEl.setAttribute('download', item.name);
    anchorEl.click();
    // Revoked on the next tick: the click has handed the URL to the download by then.
    setTimeout(() => URL.revokeObjectURL(url));
  }

  // ECDEV-431: print the attachment on screen through a hidden iframe, as the required documents'
  // preview does. Not tracked either.
  print(event?: Event): void {
    event?.stopPropagation();
    const item = this.current;
    if (!item) {
      return;
    }
    this.removePrintFrame();
    this.printUrl = URL.createObjectURL(this.blobOf(item));
    const frameEl = document.createElement('iframe');
    frameEl.style.display = 'none';
    if (this.isPdfItem(item)) {
      frameEl.src = this.printUrl;
    } else {
      // An image opened on its own prints at its natural size and spills across pages; wrapped,
      // it is scaled to fit one. The iframe's load event waits for the image.
      frameEl.srcdoc =
        '<!doctype html><html><body style="margin:0">' +
        `<img src="${this.printUrl}" style="display:block;max-width:100%;max-height:100vh;margin:auto">` +
        '</body></html>';
    }
    frameEl.onload = () => {
      frameEl.contentWindow?.focus();
      frameEl.contentWindow?.print();
    };
    document.body.appendChild(frameEl);
    this.printFrame = frameEl;
  }

  close(): void {
    this.modalState.emit({ isClose: true });
  }

  backdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'ArrowLeft') {
      this.prev();
    } else if (event.key === 'ArrowRight') {
      this.next();
    }
  }

  private revokeImageUrl(): void {
    if (this.imageUrl) {
      URL.revokeObjectURL(this.imageUrl);
      this.imageUrl = null;
    }
  }

  // The print iframe stays in the page until the next print or until the gallery closes: removing
  // it straight after print() can cancel a PDF that the browser is still spooling.
  private removePrintFrame(): void {
    this.printFrame?.remove();
    this.printFrame = null;
    if (this.printUrl) {
      URL.revokeObjectURL(this.printUrl);
      this.printUrl = null;
    }
  }

  ngOnDestroy(): void {
    this.revokeImageUrl();
    this.removePrintFrame();
  }
}
